import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { triggerWarningToast } from '@resurs/midas-react'
import { getText } from '../internal/TextLabels'
import { logout } from '../store/actions/auth-actions'

export interface AuthContextProps {
  token?: string
  isLoggedIn: boolean
  authorities: string[]
  hasAuthority: (authority: string) => boolean
}

export const AuthContext = React.createContext<AuthContextProps>({
  token: undefined,
  isLoggedIn: false,
  authorities: [],
  hasAuthority: () => false,
})

export const AuthContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [loggedInThisSession, setLoggedInThisSession] = useState(false)

  const dispatch = useDispatch()

  const { token, isLoggedIn, authorities, refreshTokenExpireTime } =
    useSelector(
      (state: {
        auth?: {
          token?: string
          isLoggedIn: boolean
          authorities?: string[]
          decodedRefreshToken?: {
            exp: number
          }
        }
      }) => ({
        token: state.auth?.token,
        isLoggedIn: !!state.auth?.isLoggedIn,
        authorities: state.auth?.authorities ?? [],
        refreshTokenExpireTime: state.auth?.decodedRefreshToken?.exp,
      }),
    )

  useEffect(() => {
    const task = () => {
      const now = Math.floor(Date.now() / 1000)
      if (refreshTokenExpireTime && now >= refreshTokenExpireTime) {
        dispatch(logout())
        if (loggedInThisSession) {
          triggerWarningToast(getText('login.sessionTimeout'))
          setLoggedInThisSession(false)
        }
      } else if (isLoggedIn && !loggedInThisSession) {
        setLoggedInThisSession(true)
      }
    }
    task()
    const intervalId = setInterval(task, 5 * 60 * 1000) // every 5 minutes
    return () => {
      clearInterval(intervalId)
    }
  }, [isLoggedIn, loggedInThisSession, refreshTokenExpireTime])

  const authValue = useMemo<AuthContextProps>(
    () => ({
      token,
      isLoggedIn,
      authorities,
      hasAuthority: authority => !!authorities?.includes(authority),
    }),
    [token, authorities],
  )

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  )
}
